import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowBack,
    CheckCircle,
    Description,
    Save,
    FileDownload,
} from "@mui/icons-material";
import {
    Alert,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    MenuItem,
    Paper,
    TextField,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import {
    COLORS,
    cardSx,
} from "../shared/FinanceUiKit";
import { toLocalDateInputValue } from "../shared/dateInput";
import { financeText } from "finance-language";
// Koyu hero bandı için (yedekten geri yüklendi)
import { ReceiptLong } from "@mui/icons-material";

const API_URL = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) =>
    Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

const normalizeCurrency = (value, fallback = "EUR") => {
    const currency = String(value || fallback).trim().toUpperCase();
    if (currency === "EURO") return "EUR";
    if (currency === "TL") return "TRY";
    return currency || fallback;
};

const formatMoney = (value, currency = "EUR") => {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: normalizeCurrency(currency),
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(toNumber(value));
    } catch {
        return `${roundMoney(value).toFixed(2)} ${normalizeCurrency(currency)}`;
    }
};

// Currencies offered by the "Display Currency" selector -- the currency the
// page's AGGREGATED figures (original/returned/remaining cards and the return
// summary) are shown in. Per-line amounts keep their own native currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

// Converts EVERY currency bucket into `targetCurrency` (even a single-entry
// bucket) and sums them. Used wherever amounts that may sit in different
// currencies are added together -- e.g. an invoice billed in EUR whose lines
// carry a USD selling currency: neither bucket alone is "mixed", but adding
// them without converting is still wrong. Returns null when a rate is
// missing, so callers can fall back to the raw (unconverted) figure.
const sumConvertedTo = (byCurrency, convert, targetCurrency = "EUR") => {
    const entries = Object.entries(byCurrency).filter(([, amount]) => amount !== 0);
    if (entries.length === 0) return 0;
    if (typeof convert !== "function") return null;

    let total = 0;

    for (const [itemCurrency, amount] of entries) {
        const converted = convert(amount, itemCurrency, targetCurrency);
        if (converted === null || converted === undefined) return null;
        total += converted;
    }

    return total;
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "-"
        : new Intl.DateTimeFormat("en-GB").format(date);
};

const getTodayValue = () => toLocalDateInputValue();

const extractRecord = (payload) =>
    payload?.data?.invoice ||
    payload?.data?.data ||
    payload?.invoice ||
    payload?.data ||
    payload;

const getItemId = (item) => String(item?._id || item?.id || "");

const getItemName = (item) =>
    item?.commodity ||
    item?.description ||
    item?.itemCode ||
    "Invoice item";

const getItemCurrency = (item, invoice) =>
    normalizeCurrency(
        item?.sellingRateCurrency ||
        item?.currency ||
        invoice?.sellingRateCurrency ||
        invoice?.currency,
    );

const getItemUnitPrice = (item) =>
    toNumber(item?.unitPrice ?? item?.sellingRate ?? item?.amount);

const getItemVatRate = (item) => toNumber(item?.vatRate);

const getReturnableQuantity = (item) =>
    Math.max(
        toNumber(
            item?.returnableQuantity ??
            item?.remainingQuantity ??
            item?.unitQuantity,
        ),
        0,
    );

const createSelectionState = (invoice) =>
    Object.fromEntries(
        (invoice?.invoiceItems || []).map((item) => [
            getItemId(item),
            {
                selected: false,
                quantity: 0,
            },
        ]),
    );

function CreateReturnInvoice() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const token = localStorage.getItem("token");

    const originalInvoiceId =
        searchParams.get("originalInvoiceId") ||
        location.state?.originalInvoiceId ||
        location.state?.invoice?._id ||
        location.state?.invoice?.id ||
        "";

    const [invoice, setInvoice] = useState(
        location.state?.returnableInvoice || null,
    );
    const [returnType, setReturnType] = useState("partial");
    const [entryMode, setEntryMode] = useState("quantity");
    const [returnReason, setReturnReason] = useState("");
    const [invoiceDate, setInvoiceDate] = useState(getTodayValue());
    const [dueDate, setDueDate] = useState("");
    const [selections, setSelections] = useState({});
    const [loadingInvoice, setLoadingInvoice] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [exchangeRates, setExchangeRates] = useState(null);
    // Display-only currency: which currency the page's aggregated amounts are
    // shown in. It never changes what is SAVED -- the return invoice itself is
    // still created in the original invoice's own currency by the backend.
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const fetchWithAuth = useCallback(
        async (path, options = {}) => {
            const response = await fetch(`${API_URL}${path}`, {
                ...options,
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(options.body
                        ? { "Content-Type": "application/json" }
                        : {}),
                    ...options.headers,
                },
            });

            const payload = await response.json().catch(() => null);

            if (!response.ok) {
                const error = new Error(
                    payload?.message ||
                    payload?.error ||
                    financeText("The request could not be completed."),
                );
                error.status = response.status;
                error.code = payload?.code;
                throw error;
            }

            return payload;
        },
        [token],
    );

    const applyReturnType = useCallback((type, sourceInvoice) => {
        setReturnType(type);
        setSelections((previous) =>
            Object.fromEntries(
                (sourceInvoice?.invoiceItems || []).map((item) => {
                    const id = getItemId(item);
                    const available = getReturnableQuantity(item);

                    return [
                        id,
                        type === "full"
                            ? {
                                selected: available > 0,
                                quantity: available,
                            }
                            : previous[id] || {
                            selected: false,
                            quantity: 0,
                        },
                    ];
                }),
            ),
        );
    }, []);

    const loadInvoice = useCallback(async () => {
        if (!originalInvoiceId) {
            setLoadingInvoice(false);
            setErrorMessage(
                financeText("Original invoice ID is missing. Open this page from an invoice detail screen."),
            );
            return;
        }

        try {
            setLoadingInvoice(true);
            setErrorMessage("");

            const payload = await fetchWithAuth(
                `/finance/invoices/${originalInvoiceId}/returnable`,
            );
            const record = extractRecord(payload);

            if (!record || !Array.isArray(record.invoiceItems)) {
                throw new Error(financeText("Returnable invoice data is invalid."));
            }

            setInvoice(record);
            setSelections(createSelectionState(record));
        } catch (error) {
            console.error("Returnable invoice load error:", error);
            setErrorMessage(
                error.message || financeText("Returnable invoice could not be loaded."),
            );
        } finally {
            setLoadingInvoice(false);
        }
    }, [fetchWithAuth, originalInvoiceId]);

    useEffect(() => {
        loadInvoice();
    }, [loadInvoice]);

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    // Returns null (rather than a silently wrong number) when a rate is
    // missing, so callers can fall back to the native figure.
    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = toNumber(amount);
            const from = normalizeCurrency(fromCurrency, "");
            const to = normalizeCurrency(toCurrency, "");

            if (!from || !to || from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const currency = normalizeCurrency(
        invoice?.sellingRateCurrency || invoice?.currency,
    );

    // Converts one amount into the selected display currency, falling back to
    // the amount in its own currency when no rate is available.
    const toDisplayAmount = useCallback(
        (amount, fromCurrency = currency) => {
            const converted = convertCurrency(amount, fromCurrency, selectedCurrency);

            return converted === null
                ? { value: toNumber(amount), currency: normalizeCurrency(fromCurrency) }
                : { value: converted, currency: normalizeCurrency(selectedCurrency) };
        },
        [convertCurrency, selectedCurrency, currency],
    );

    const originalTotal = toNumber(
        invoice?.originalInvoiceTotal ??
        invoice?.totalAmount ??
        invoice?.invoiceSalesAmount,
    );
    const reservedReturnAmount = toNumber(
        invoice?.reservedReturnAmount ?? invoice?.returnedAmount,
    );
    const remainingReturnableAmount = Math.max(
        toNumber(
            invoice?.returnableAmount ??
            originalTotal - reservedReturnAmount,
        ),
        0,
    );

    const selectedItems = useMemo(
        () =>
            (invoice?.invoiceItems || [])
                .map((item) => {
                    const id = getItemId(item);
                    const selection = selections[id] || {};
                    const quantity = toNumber(selection.quantity);
                    const unitPrice = getItemUnitPrice(item);
                    const vatRate = getItemVatRate(item);
                    const amount = roundMoney(quantity * unitPrice);
                    const vatAmount = roundMoney(amount * (vatRate / 100));

                    return {
                        item,
                        id,
                        selected: Boolean(selection.selected),
                        quantity,
                        amount,
                        vatAmount,
                        total: roundMoney(amount + vatAmount),
                    };
                })
                .filter((row) => row.selected && row.quantity > 0),
        [invoice, selections],
    );

    // Selected lines grouped by their OWN selling currency. Invoice lines can
    // carry a currency of their own (see getItemCurrency), so they must never
    // be added together raw.
    const selectedTotalsByCurrency = useMemo(() => {
        const subtotal = {};
        const vat = {};
        const total = {};

        selectedItems.forEach((row) => {
            const rowCurrency = getItemCurrency(row.item, invoice);
            subtotal[rowCurrency] = (subtotal[rowCurrency] || 0) + row.amount;
            vat[rowCurrency] = (vat[rowCurrency] || 0) + row.vatAmount;
            total[rowCurrency] = (total[rowCurrency] || 0) + row.total;
        });

        return { subtotal, vat, total };
    }, [selectedItems, invoice]);

    // Authoritative totals in the ORIGINAL invoice's currency. These drive
    // validation and the save button, both of which compare against
    // remainingReturnableAmount (also in the invoice's currency), so they must
    // NOT follow the display selector. Lines quoted in another currency are
    // converted into the invoice currency first; without a rate this falls
    // back to the old raw sum.
    const totals = useMemo(() => {
        const raw = selectedItems.reduce(
            (result, row) => ({
                subtotal: roundMoney(result.subtotal + row.amount),
                vat: roundMoney(result.vat + row.vatAmount),
                total: roundMoney(result.total + row.total),
            }),
            { subtotal: 0, vat: 0, total: 0 },
        );

        return {
            subtotal: roundMoney(
                sumConvertedTo(selectedTotalsByCurrency.subtotal, convertCurrency, currency) ??
                raw.subtotal,
            ),
            vat: roundMoney(
                sumConvertedTo(selectedTotalsByCurrency.vat, convertCurrency, currency) ??
                raw.vat,
            ),
            total: roundMoney(
                sumConvertedTo(selectedTotalsByCurrency.total, convertCurrency, currency) ??
                raw.total,
            ),
        };
    }, [selectedItems, selectedTotalsByCurrency, convertCurrency, currency]);

    // The same totals converted into the user's chosen display currency (the
    // only version that is rendered). Falls back to the invoice-currency
    // figures when a rate is missing, so the summary never mislabels e.g. a
    // TRY total as EUR.
    const displayTotals = useMemo(() => {
        const subtotal = sumConvertedTo(
            selectedTotalsByCurrency.subtotal,
            convertCurrency,
            selectedCurrency,
        );
        const vat = sumConvertedTo(
            selectedTotalsByCurrency.vat,
            convertCurrency,
            selectedCurrency,
        );
        const total = sumConvertedTo(
            selectedTotalsByCurrency.total,
            convertCurrency,
            selectedCurrency,
        );

        if (subtotal === null || vat === null || total === null) {
            return { ...totals, currency };
        }

        return {
            subtotal: roundMoney(subtotal),
            vat: roundMoney(vat),
            total: roundMoney(total),
            currency: normalizeCurrency(selectedCurrency),
        };
    }, [selectedTotalsByCurrency, convertCurrency, selectedCurrency, totals, currency]);

    const isFullyReturned =
        !loadingInvoice &&
        remainingReturnableAmount <= 0.009;


    const selectableItems = useMemo(
        () => (invoice?.invoiceItems || []).filter((item) => getReturnableQuantity(item) > 0),
        [invoice],
    );

    const allSelected = selectableItems.length > 0 && selectableItems.every(
        (item) => Boolean(selections[getItemId(item)]?.selected),
    );

    const toggleSelectAll = (checked) => {
        if (returnType === "full" || isFullyReturned || saving) return;

        setSelections((previous) => {
            const next = { ...previous };
            selectableItems.forEach((item) => {
                const id = getItemId(item);
                const available = getReturnableQuantity(item);
                const unitPrice = getItemUnitPrice(item);
                next[id] = checked
                    ? { selected: true, quantity: available, amount: roundMoney(available * unitPrice) }
                    : { selected: false, quantity: 0, amount: 0 };
            });
            return next;
        });
    };

    const exportToExcel = async () => {
        try {
            const XLSX = await import("xlsx");
            const rows = (invoice?.invoiceItems || []).map((item) => {
                const id = getItemId(item);
                const selection = selections[id] || {};
                const remainingQty = getReturnableQuantity(item);
                const unitPrice = getItemUnitPrice(item);

                return {
                    Selected: selection.selected ? "Yes" : "No",
                    Item: getItemName(item),
                    Code: item?.itemCode || "",
                    "Original Quantity": toNumber(item?.originalQuantity ?? item?.unitQuantity),
                    "Returned Quantity": toNumber(item?.returnedQuantity),
                    "Remaining Quantity": remainingQty,
                    "Remaining Amount": roundMoney(remainingQty * unitPrice),
                    "Return Quantity": toNumber(selection.quantity),
                    "Return Amount": roundMoney(toNumber(selection.quantity) * unitPrice),
                    Currency: getItemCurrency(item, invoice),
                    "Unit Price": unitPrice,
                    "VAT %": getItemVatRate(item),
                };
            });
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Return Credit Note");
            XLSX.writeFile(workbook, `Return-Credit-Note-${invoice?.invoiceNo || "Draft"}.xlsx`);
        } catch (error) {
            console.error("Excel export error:", error);
            setErrorMessage(financeText("Excel export could not be created. Make sure the xlsx package is installed."));
        }
    };

    const returnItemColumns = useMemo(
        () => [
            { Header: financeText("Return"), accessor: "select", width: "7%", align: "center" },
            { Header: financeText("Item"), accessor: "item", width: "25%" },
            { Header: financeText("Original Qty"), accessor: "originalQty", align: "right" },
            { Header: financeText("Returned"), accessor: "returnedQty", align: "right" },
            { Header: financeText("Remaining Qty"), accessor: "available", align: "right" },
            { Header: financeText("Remaining Amount"), accessor: "remainingAmount", align: "right" },
            { Header: financeText(entryMode === "quantity" ? "Return Qty" : "Return Amount"), accessor: "returnQty", align: "center" },
            { Header: financeText("Unit Price"), accessor: "unitPrice", align: "right" },
            { Header: financeText("VAT"), accessor: "vat", align: "center" },
            { Header: financeText("Return Total"), accessor: "returnTotal", align: "right" },
        ],
        [entryMode],
    );

    const returnItemRows = useMemo(
        () =>
            (invoice?.invoiceItems || []).map((item) => {
                const id = getItemId(item);
                const selection = selections[id] || {};
                const available = getReturnableQuantity(item);
                const quantity = toNumber(selection.quantity);
                const enteredAmount = toNumber(selection.amount);
                const unitPrice = getItemUnitPrice(item);
                const vatRate = getItemVatRate(item);
                const lineTotal = roundMoney(
                    quantity * unitPrice * (1 + vatRate / 100),
                );
                const itemCurrency = getItemCurrency(item, invoice);
                const remainingAmount = roundMoney(available * unitPrice);
                const disabled = available <= 0 || isFullyReturned || saving;

                return {
                    select: (
                        <Checkbox
                            checked={Boolean(selection.selected)}
                            disabled={disabled || returnType === "full"}
                            onChange={(event) =>
                                updateSelection(item, {
                                    selected: event.target.checked,
                                })
                            }
                            inputProps={{
                                "aria-label": `${financeText("Select")} ${getItemName(item)}`,
                            }}
                        />
                    ),
                    item: (
                        <MDBox minWidth="220px">
                            <MDTypography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {getItemName(item)}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color: COLORS.subtext,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                }}
                            >
                                {item?.itemCode || "-"}
                                {item?.description ? ` · ${item.description}` : ""}
                            </MDTypography>
                        </MDBox>
                    ),
                    originalQty: (
                        <MDTypography variant="body2" sx={{ color: COLORS.text }}>
                            {toNumber(item?.originalQuantity ?? item?.unitQuantity)}
                        </MDTypography>
                    ),
                    returnedQty: (
                        <MDTypography variant="body2" sx={{ color: COLORS.text }}>
                            {toNumber(item?.returnedQuantity)}
                        </MDTypography>
                    ),
                    available: (
                        <Chip
                            size="small"
                            label={available}
                            color={available > 0 ? "success" : "default"}
                            sx={{ fontWeight: 700 }}
                        />
                    ),
                    remainingAmount: (
                        <MDTypography variant="body2" fontWeight="bold" sx={{ color: COLORS.green, whiteSpace: "nowrap" }}>
                            {formatMoney(remainingAmount, itemCurrency)}
                        </MDTypography>
                    ),
                    returnQty: (
                        <TextField
                            size="small"
                            type="number"
                            value={entryMode === "quantity" ? quantity : enteredAmount}
                            disabled={disabled || returnType === "full"}
                            onChange={(event) =>
                                updateSelection(
                                    item,
                                    entryMode === "quantity"
                                        ? { quantity: event.target.value }
                                        : { amount: event.target.value },
                                )
                            }
                            inputProps={{
                                min: 0,
                                max: entryMode === "quantity" ? available : remainingAmount,
                                step: "any",
                                style: { textAlign: "right" },
                            }}
                            error={quantity > available || enteredAmount > remainingAmount}
                            sx={{ width: 110 }}
                        />
                    ),
                    unitPrice: (
                        <MDTypography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: COLORS.text, whiteSpace: "nowrap" }}
                        >
                            {formatMoney(unitPrice, itemCurrency)}
                        </MDTypography>
                    ),
                    vat: (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`%${vatRate}`}
                            sx={{ fontWeight: 700 }}
                        />
                    ),
                    returnTotal: (
                        <MDTypography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                                color: quantity > 0 ? COLORS.green : COLORS.text,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {formatMoney(lineTotal, itemCurrency)}
                        </MDTypography>
                    ),
                };
            }),
        [
            invoice,
            selections,
            returnType,
            saving,
            isFullyReturned,
        ],
    );


    const updateSelection = (item, changes) => {
        if (returnType === "full") return;

        const id = getItemId(item);
        const available = getReturnableQuantity(item);

        setSelections((previous) => {
            const current = previous[id] || { selected: false, quantity: 0 };
            const next = { ...current, ...changes };

            if (Object.prototype.hasOwnProperty.call(changes, "selected")) {
                next.quantity = changes.selected
                    ? (current.quantity > 0 ? current.quantity : available)
                    : 0;
                next.amount = changes.selected
                    ? roundMoney(next.quantity * getItemUnitPrice(item))
                    : 0;
            }

            if (Object.prototype.hasOwnProperty.call(changes, "amount")) {
                const unitPrice = getItemUnitPrice(item);
                const maxAmount = roundMoney(available * unitPrice);
                next.amount = Math.min(Math.max(toNumber(changes.amount), 0), maxAmount);
                next.quantity = unitPrice > 0 ? Math.min(next.amount / unitPrice, available) : 0;
                next.selected = next.amount > 0;
            }

            if (Object.prototype.hasOwnProperty.call(changes, "quantity")) {
                next.quantity = Math.min(
                    Math.max(toNumber(changes.quantity), 0),
                    available,
                );
                next.amount = roundMoney(next.quantity * getItemUnitPrice(item));
                next.selected = next.quantity > 0;
            }

            return {
                ...previous,
                [id]: next,
            };
        });
    };

    const validate = () => {
        if (!originalInvoiceId || !invoice) {
            return financeText("Original invoice could not be found.");
        }
        if (!["full", "partial"].includes(returnType)) {
            return financeText("Return type is required.");
        }
        if (!returnReason.trim()) {
            return financeText("Return reason is required.");
        }
        if (!invoiceDate) {
            return financeText("Return invoice date is required.");
        }
        if (!selectedItems.length) {
            return financeText("Select at least one invoice item.");
        }

        const invalidRow = selectedItems.find(({ item, quantity }) => {
            const available = getReturnableQuantity(item);
            return quantity <= 0 || quantity > available;
        });

        if (invalidRow) {
            return `${financeText('Return quantity for "')}${getItemName(invalidRow.item)}${financeText('" is invalid.')}`;
        }
        if (totals.total <= 0) {
            return financeText("Return total must be greater than zero.");
        }
        if (totals.total > remainingReturnableAmount + 0.01) {
            return financeText("Return total exceeds the remaining returnable amount.");
        }

        return "";
    };

    const saveReturnInvoice = async () => {
        const validationMessage = validate();

        if (validationMessage) {
            setErrorMessage(validationMessage);
            return;
        }

        try {
            setSaving(true);
            setErrorMessage("");
            setSuccessMessage("");

            const payload = {
                originalInvoiceId,
                returnType,
                returnEntryMode: entryMode,
                returnReason: returnReason.trim(),
                invoiceDate,
                dueDate: dueDate || null,
                items: selectedItems.map(({ id, quantity }) => ({
                    originalInvoiceItemId: id,
                    quantity,
                })),
            };

            const response = await fetchWithAuth("/finance/return-invoices", {
                method: "POST",
                body: JSON.stringify(payload),
            });
            const savedInvoice = extractRecord(response);
            const savedId = savedInvoice?._id || savedInvoice?.id;

            setSuccessMessage(financeText("Return invoice draft created successfully."));

            setTimeout(() => {
                navigate(
                    savedId
                        ? `/invoice/finance/return-invoices/${savedId}`
                        : `/invoice/finance/jobs/${originalInvoiceId}`,
                    {
                        replace: true,
                        state: {
                            createdReturnInvoiceId: savedId,
                            originalInvoiceId,
                        },
                    },
                );
            }, 500);
        } catch (error) {
            console.error("Return invoice create error:", error);
            setErrorMessage(
                error.message || financeText("Return invoice could not be created."),
            );
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate(`/invoice/finance/jobs/${originalInvoiceId}`);
    };

    if (loadingInvoice) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox
                    minHeight="70vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    gap={2}
                >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text">
                        {financeText("Loading returnable invoice...")}
                    </MDTypography>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <MDBox
                    mb={3}
                    p={{ xs: 2.5, md: 3.5 }}
                    borderRadius="20px"
                    sx={{
                        color: COLORS.white,
                        background:
                            "linear-gradient(135deg, #0B1F3A 0%, #175CD3 100%)",
                        boxShadow: "0 16px 40px rgba(11,31,58,.16)",
                    }}
                >
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        flexDirection={{ xs: "column", md: "row" }}
                        gap={2}
                    >
                        <MDBox>
                            <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                <ReceiptLong />
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        letterSpacing: ".1em",
                                    }}
                                >
                                    {financeText("FINANCE RETURN OPERATIONS")}
                                </MDTypography>
                            </MDBox>
                            <MDTypography
                                variant="h3"
                                fontWeight="bold"
                                sx={{ color: COLORS.white }}
                            >
                                {financeText("Create Return Invoice")}
                            </MDTypography>
                            <MDTypography
                                variant="body2"
                                mt={1}
                                sx={{ color: "rgba(255,255,255,.72)" }}
                            >
                                {financeText("Create a full or partial return from the remaining quantities of the original invoice.")}
                            </MDTypography>
                        </MDBox>

                        <Chip
                            label={financeText("Draft Return Invoice")}
                            sx={{
                                color: COLORS.white,
                                background: "rgba(255,255,255,.12)",
                                border: "1px solid rgba(255,255,255,.18)",
                                fontWeight: 700,
                            }}
                        />
                    </MDBox>
                </MDBox>

                {errorMessage ? (
                    <Alert
                        severity="error"
                        onClose={() => setErrorMessage("")}
                        sx={{ mb: 2, borderRadius: "8px" }}
                    >
                        {errorMessage}
                    </Alert>
                ) : null}

                {successMessage ? (
                    <Alert
                        severity="success"
                        sx={{ mb: 2, borderRadius: "8px" }}
                    >
                        {successMessage}
                    </Alert>
                ) : null}

                {isFullyReturned ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: "8px" }}>
                        {financeText("This invoice has no remaining returnable amount.")}
                    </Alert>
                ) : null}

                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <Card sx={cardSx}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Description sx={{ color: COLORS.blue }} />
                                        <MDBox>
                                            <MDTypography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ color: COLORS.text }}
                                            >
                                                {financeText("Original Invoice")}
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText("Source invoice and remaining return capacity")}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    {/* Display-only: converts the summary cards and the
                                        return summary into this currency. The return
                                        invoice is still created in the original
                                        invoice's own currency. */}
                                    <TextField
                                        select
                                        size="small"
                                        label={financeText("Display Currency")}
                                        value={selectedCurrency}
                                        onChange={(event) => setSelectedCurrency(event.target.value)}
                                        sx={{
                                            minWidth: 160,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "11px",
                                                background: "#FFFFFF",
                                            },
                                        }}
                                    >
                                        {CURRENCY_OPTIONS.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </MDBox>

                                <Grid container spacing={2}>
                                    {[
                                        ["Invoice No", invoice?.invoiceNo || "-"],
                                        [
                                            invoice?.invoiceType === "purchase"
                                                ? "Supplier"
                                                : "Customer",
                                            invoice?.invoiceType === "purchase"
                                                ? invoice?.supplierName || "-"
                                                : invoice?.customerName || "-",
                                        ],
                                        ["Invoice Date", formatDate(invoice?.invoiceDate)],
                                        ["Currency", currency],
                                    ].map(([label, value]) => (
                                        <Grid item xs={12} sm={6} md={3} key={label}>
                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText(label)}
                                            </MDTypography>
                                            <MDTypography
                                                variant="body2"
                                                fontWeight="bold"
                                                sx={{ color: COLORS.text }}
                                            >
                                                {value}
                                            </MDTypography>
                                        </Grid>
                                    ))}
                                </Grid>

                                <Divider sx={{ my: 2.5 }} />

                                <Grid container spacing={2}>
                                    {[
                                        {
                                            label: "Original Total",
                                            value: originalTotal,
                                            color: COLORS.navy,
                                            background: COLORS.blueLight,
                                        },
                                        {
                                            label: "Reserved / Returned",
                                            value: reservedReturnAmount,
                                            color: COLORS.amber,
                                            background: COLORS.amberLight,
                                        },
                                        {
                                            label: "Remaining Returnable",
                                            value: remainingReturnableAmount,
                                            color: COLORS.green,
                                            background: COLORS.greenLight,
                                        },
                                    ].map((item) => {
                                        const display = toDisplayAmount(item.value);

                                        return (
                                        <Grid item xs={12} md={4} key={item.label}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: "8px",
                                                    background: item.background,
                                                    border: `1px solid ${COLORS.border}`,
                                                }}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(item.label)}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="h5"
                                                    fontWeight="bold"
                                                    sx={{ color: item.color }}
                                                >
                                                    {formatMoney(display.value, display.currency)}
                                                </MDTypography>
                                            </Paper>
                                        </Grid>
                                        );
                                    })}
                                </Grid>
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={cardSx}>
                            <MDBox p={3}>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText("Return Information")}
                                </MDTypography>
                                <MDTypography
                                    variant="caption"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Select the return type and provide a mandatory reason")}
                                </MDTypography>

                                <Grid container spacing={2} mt={0.5}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Type")}
                                            value={returnType}
                                            disabled={isFullyReturned || saving}
                                            onChange={(event) =>
                                                applyReturnType(event.target.value, invoice)
                                            }
                                        >
                                            <MenuItem value="partial">
                                                {financeText("Partial Return")}
                                            </MenuItem>
                                            <MenuItem value="full">
                                                {financeText("Full Remaining Return")}
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label={financeText("Return Invoice Date")}
                                            value={invoiceDate}
                                            disabled={isFullyReturned || saving}
                                            onChange={(event) =>
                                                setInvoiceDate(event.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label={financeText("Due Date")}
                                            value={dueDate}
                                            disabled={isFullyReturned || saving}
                                            onChange={(event) =>
                                                setDueDate(event.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Original Invoice No")}
                                            value={invoice?.invoiceNo || ""}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            multiline
                                            minRows={3}
                                            label={financeText("Return Reason")}
                                            value={returnReason}
                                            disabled={isFullyReturned || saving}
                                            inputProps={{ maxLength: 1000 }}
                                            helperText={`${returnReason.length}/1000`}
                                            onChange={(event) =>
                                                setReturnReason(event.target.value)
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={cardSx}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    mb={2}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Return Items")}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Price, currency and VAT are locked to the original invoice.")}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={entryMode}
                                            onChange={(_, value) => value && setEntryMode(value)}
                                        >
                                            <ToggleButton value="quantity">{financeText("Quantity Based")}</ToggleButton>
                                            <ToggleButton value="amount">{financeText("Amount Based")}</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Tooltip title={financeText("Select or clear every returnable row")}>
                                            <span>
                                                <MDButton
                                                    variant="outlined"
                                                    color="info"
                                                    size="small"
                                                    disabled={returnType === "full" || isFullyReturned}
                                                    onClick={() => toggleSelectAll(!allSelected)}
                                                >
                                                    {allSelected ? financeText("Clear All") : financeText("Select All")}
                                                </MDButton>
                                            </span>
                                        </Tooltip>
                                        <MDButton
                                            variant="outlined"
                                            color="success"
                                            size="small"
                                            startIcon={<FileDownload />}
                                            onClick={exportToExcel}
                                        >
                                            {financeText("Excel Export")}
                                        </MDButton>
                                        <Chip
                                            size="small"
                                            label={returnType === "full" ? financeText("All remaining selected") : `${selectedItems.length} ${financeText("selected")}`}
                                            color={selectedItems.length ? "secondary" : "default"}
                                        />
                                    </MDBox>
                                </MDBox>

                                <DataTable
                                    table={{ columns: returnItemColumns, rows: returnItemRows }}
                                    isSorted={false}
                                    entriesPerPage={{
                                        defaultValue: 10,
                                        entries: [5, 10, 20, 50],
                                    }}
                                    showTotalEntries
                                    noEndBorder
                                />
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <Alert severity="info" sx={{ borderRadius: "8px" }}>
                            {financeText("The backend recalculates every return line from the original invoice. Unit price, VAT, currency and totals shown here are previews and are not submitted as authoritative values.")}
                        </Alert>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Card sx={cardSx}>
                            <MDBox p={3}>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText("Return Summary")}
                                </MDTypography>
                                <Divider sx={{ my: 2 }} />

                                {[
                                    ["Subtotal", displayTotals.subtotal],
                                    ["VAT", displayTotals.vat],
                                ].map(([label, value]) => (
                                    <MDBox
                                        key={label}
                                        display="flex"
                                        justifyContent="space-between"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="body2"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText(label)}
                                        </MDTypography>
                                        <MDTypography
                                            variant="body2"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {formatMoney(value, displayTotals.currency)}
                                        </MDTypography>
                                    </MDBox>
                                ))}

                                <Divider sx={{ my: 2 }} />

                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.text }}
                                    >
                                        {financeText("Return Total")}
                                    </MDTypography>
                                    <MDTypography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.blue }}
                                    >
                                        {formatMoney(displayTotals.total, displayTotals.currency)}
                                    </MDTypography>
                                </MDBox>

                                {totals.total > remainingReturnableAmount + 0.01 ? (
                                    <Alert
                                        severity="error"
                                        sx={{ mt: 2, borderRadius: "10px" }}
                                    >
                                        {financeText("The selected total exceeds the remaining returnable amount.")}
                                    </Alert>
                                ) : null}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>

                <MDBox
                    mt={3}
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={1.5}
                >
                    <MDButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<ArrowBack />}
                        disabled={saving}
                        onClick={goBack}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="gradient"
                        color="info"
                        startIcon={
                            saving
                                ? <CircularProgress size={18} color="inherit" />
                                : successMessage
                                    ? <CheckCircle />
                                    : <Save />
                        }
                        disabled={
                            saving ||
                            isFullyReturned ||
                            totals.total <= 0 ||
                            totals.total > remainingReturnableAmount + 0.01
                        }
                        onClick={saveReturnInvoice}
                    >
                        {saving ? financeText("Creating...") : financeText("Create Return Draft")}
                    </MDButton>
                </MDBox>
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
}

export default CreateReturnInvoice;